/**
 * Напоминания устройства.
 *
 * «Напоминания» на iPhone — это дела, а не встречи: у них есть срок, но нет
 * длительности, и их закрывают галочкой. То же самое, что задача здесь, — и
 * поэтому они становятся задачами, а не встречами в сетке дня.
 *
 * ## Почему только iPhone
 *
 * Проверено по самому плагину: все операции с напоминаниями помечены
 * `@platform iOS`. Дело не в плагине — на Android системных задач в календаре
 * нет вовсе. «Задачи» в приложении Google Календарь это отдельный сервис
 * Google Tasks со своим API и своим входом, локально он недоступен.
 *
 * Обещать здесь кросс-платформенность значило бы оставить человека с
 * настройкой, которая молча ничего не делает.
 */

import { Capacitor } from '@capacitor/core';
import type { CapacitorCalendarPlugin } from '@ebarooni/capacitor-calendar';

import { type IsoDate, type MinutesOfDay, momentAt, toIsoDate } from '@/shared/lib/datetime';

import type { CalendarAccess } from './system-calendar';

export interface ReminderList {
  id: string;
  title: string;
}

/** Напоминание устройства в том виде, в каком оно нужно сверке. */
export interface SystemReminder {
  id: string;
  listId: string | null;
  title: string;
  notes: string;
  /** Срок. `null` — напоминание без срока. */
  dueDate: IsoDate | null;
  /** Время внутри дня, если оно указано. */
  dueMin: MinutesOfDay | null;
  completed: boolean;
}

/** Поля напоминания, которые синхронизируются. */
export interface ReminderFields {
  title: string;
  notes: string;
  dueDate: IsoDate | null;
  dueMin: MinutesOfDay | null;
  completed: boolean;
}

export interface RemindersPort {
  available(): boolean;
  checkAccess(): Promise<CalendarAccess>;
  requestAccess(): Promise<CalendarAccess>;
  listLists(): Promise<ReminderList[]>;
  listReminders(listIds: string[]): Promise<SystemReminder[]>;
  updateReminder(id: string, fields: ReminderFields): Promise<void>;
}

/**
 * Напоминание устройства в наши понятия.
 *
 * Срок без времени Apple хранит полуночью — и «до конца дня» здесь честнее,
 * чем «в 00:00»: человек не ставил себе дело на полночь, он просто указал
 * день.
 */
export function toSystemReminder(raw: {
  id: string;
  listId: string | null;
  title: string | null;
  notes: string | null;
  dueDate: number | null;
  isCompleted: boolean;
}): SystemReminder {
  const due = raw.dueDate === null ? null : new Date(raw.dueDate);
  const minutes = due === null ? 0 : due.getHours() * 60 + due.getMinutes();

  return {
    id: raw.id,
    listId: raw.listId,
    title: raw.title?.trim() || 'Без названия',
    notes: raw.notes ?? '',
    dueDate: due === null ? null : toIsoDate(due),
    dueMin: due === null || minutes === 0 ? null : minutes,
    completed: raw.isCompleted,
  };
}

/** Напоминаний на этом устройстве нет: Android или вкладка браузера. */
export const unavailableReminders: RemindersPort = {
  available: () => false,
  checkAccess: () => Promise.resolve('unavailable'),
  requestAccess: () => Promise.resolve('unavailable'),
  listLists: () => Promise.resolve([]),
  listReminders: () => Promise.resolve([]),
  updateReminder: () => Promise.reject(new Error('Напоминания недоступны')),
};

export const nativeReminders: RemindersPort = {
  available: () => Capacitor.getPlatform() === 'ios',

  async checkAccess() {
    const plugin = (await load())?.plugin;
    if (!plugin) return 'unavailable';

    /*
      Состояния читаются из обоих мест по той же причине, что и у календаря:
      одна платформа кладёт их на верхний уровень ответа, другая — во
      вложенный `result`.
    */
    const answer = (await plugin.checkAllPermissions()) as unknown as Record<string, unknown>;
    const states = { ...answer, ...((answer.result ?? {}) as Record<string, unknown>) };
    return toAccess(states.readReminders, states.writeReminders);
  },

  async requestAccess() {
    const plugin = (await load())?.plugin;
    if (!plugin) return 'unavailable';

    const result = await plugin.requestFullRemindersAccess();
    return toAccess(result.result, result.result);
  },

  async listLists() {
    const plugin = (await load())?.plugin;
    if (!plugin) return [];

    const { result } = await plugin.getRemindersLists();
    return result.map((list) => ({ id: list.id, title: list.title }));
  },

  async listReminders(listIds) {
    const plugin = (await load())?.plugin;
    if (!plugin || listIds.length === 0) return [];

    const { result } = await plugin.getRemindersFromLists({ listIds });
    return result.map(toSystemReminder);
  },

  async updateReminder(id, fields) {
    const plugin = (await load())?.plugin;
    if (!plugin) throw new Error('Напоминания недоступны');

    await plugin.modifyReminder({
      id,
      title: fields.title,
      notes: fields.notes,
      isCompleted: fields.completed,
      ...(fields.dueDate === null ? {} : { dueDate: toEpoch(fields.dueDate, fields.dueMin) }),
    });
  },
};

/** Срок без времени — начало дня. */
function toEpoch(date: IsoDate, minutes: MinutesOfDay | null): number {
  return momentAt(date, minutes ?? 0);
}

/**
 * Плагин или `null`: на Android и в браузере напоминаний не существует.
 *
 * Завёрнут по той же причине, что и календарь: интерфейс плагина отвечает и на
 * `then`, а значит, вернуть его из асинхронной функции — значит уронить вызов
 * на попытке развернуть «обещание».
 */
async function load(): Promise<{ plugin: CapacitorCalendarPlugin } | null> {
  if (Capacitor.getPlatform() !== 'ios') return null;

  const { CapacitorCalendar } = await import('@ebarooni/capacitor-calendar');
  return { plugin: CapacitorCalendar };
}

function toAccess(read: unknown, write: unknown): CalendarAccess {
  if (read === 'granted' && write === 'granted') return 'granted';
  if (read === 'denied' || write === 'denied') return 'denied';
  return 'prompt';
}

/** Напоминания устройства, если они есть; иначе честная заглушка. */
export function systemReminders(): RemindersPort {
  return Capacitor.getPlatform() === 'ios' ? nativeReminders : unavailableReminders;
}
